"use client";
import { createContext, useContext, useState, useCallback, useMemo } from "react";

export type AffectedSurface = "floor" | "walls" | "ceiling";
export type MaterialType = "drywall" | "plaster" | "hardwood" | "tile" | "carpet" | "concrete";

export interface Room {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  affectedSurfaces: AffectedSurface[];
  waterHeightIn: number;
  materialType: MaterialType;
}

export interface RoomSummary {
  totalFloorSqFt: number;
  totalWallSqFt: number;
  totalCeilingSqFt: number;
  totalVolumeFt3: number;
  totalStandingGal: number;
  totalPerimeterFt: number;
}

interface RoomContextType {
  rooms: Room[];
  addRoom: (r: Omit<Room, "id">) => void;
  updateRoom: (id: string, patch: Partial<Omit<Room, "id">>) => void;
  removeRoom: (id: string) => void;
  summary: RoomSummary;
  hasRooms: boolean;
}

const RoomContext = createContext<RoomContextType | null>(null);

function computeSummary(rooms: Room[]): RoomSummary {
  let totalFloorSqFt = 0;
  let totalWallSqFt = 0;
  let totalCeilingSqFt = 0;
  let totalVolumeFt3 = 0;
  let totalStandingGal = 0;
  let totalPerimeterFt = 0;

  for (const r of rooms) {
    const { length: L, width: W, height: H, affectedSurfaces, waterHeightIn } = r;
    totalVolumeFt3 += L * W * H;

    if (affectedSurfaces.includes("floor")) {
      totalFloorSqFt += L * W;
      totalStandingGal += L * W * waterHeightIn * 0.62338;
    }
    if (affectedSurfaces.includes("walls")) {
      totalWallSqFt += 2 * (L + W) * H;
      totalPerimeterFt += 2 * (L + W);
    }
    if (affectedSurfaces.includes("ceiling")) {
      totalCeilingSqFt += L * W;
    }
  }

  return { totalFloorSqFt, totalWallSqFt, totalCeilingSqFt, totalVolumeFt3, totalStandingGal, totalPerimeterFt };
}

export function RoomProvider({ children }: { children: React.ReactNode }) {
  const [rooms, setRooms] = useState<Room[]>([]);

  const addRoom = useCallback((r: Omit<Room, "id">) => {
    const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    setRooms((prev) => [...prev, { ...r, id }]);
  }, []);

  const updateRoom = useCallback((id: string, patch: Partial<Omit<Room, "id">>) => {
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRoom = useCallback((id: string) => {
    setRooms((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const summary = useMemo(() => computeSummary(rooms), [rooms]);
  const hasRooms = rooms.length > 0;

  const value = useMemo<RoomContextType>(
    () => ({ rooms, addRoom, updateRoom, removeRoom, summary, hasRooms }),
    [rooms, addRoom, updateRoom, removeRoom, summary, hasRooms]
  );

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export function useRooms(): RoomContextType {
  const ctx = useContext(RoomContext);
  if (!ctx) throw new Error("useRooms must be used inside <RoomProvider>");
  return ctx;
}
